import {Inject, LOCALE_ID, Pipe, PipeTransform} from '@angular/core';
import moment from 'moment';
import {formatNumber} from '@angular/common';
import {FrontIziviaTranslateService} from '../services/i18n/iziviaTranslate.service';
import DurationConstructor = moment.unitOfTime.DurationConstructor;

@Pipe({
    name: 'duration'
})
export class DurationPipe implements PipeTransform {
    readonly dayUnit: string;
    readonly hourUnit: string;
    readonly minUnit: string;
    readonly secondsUnit: string;


    constructor(@Inject(LOCALE_ID) private locale: string, private translateService: FrontIziviaTranslateService) {
        this.dayUnit = this.translateService.translate('duration.short.days');
        this.hourUnit = this.translateService.translate('duration.short.hours');
        this.minUnit = this.translateService.translate('duration.short.minutes');
        this.secondsUnit = this.translateService.translate('duration.short.seconds');
    }

    transform(value: number, param: DurationPipeParam = new DurationPipeParam()): string {
        // convert the value into a moment duration
        const duration = moment.duration(value, param.entryUnit || 's');

        // get days of the duration
        let days = Math.trunc(duration.asDays());

        // get convert duration in hours and keep only the hour unit
        let hours = Math.trunc(duration.as('hours'));

        // get minutes of the duration
        let minutes = duration.minutes();

        const formattedDuration: Array<string> = [];

        if (param.days && days) {
            formattedDuration.push(formatNumber(days, this.locale) + this.dayUnit);
            if (param.fullDecomposition) {
                hours = Math.trunc(moment.duration(value, param.entryUnit || 's')
                    .subtract(days, 'day')
                    .hours()
                );
                minutes = Math.trunc(moment.duration(value, param.entryUnit || 's')
                    .subtract(days, 'day')
                    .minutes()
                );
            }
        }

        // format hours => example 1000 will become `1 000` in french
        if (hours > 0) {
            formattedDuration.push(formatNumber(hours, this.locale, '1.0-0') + this.hourUnit);
            if (param.fullDecomposition) {
                minutes = Math.trunc(moment.duration(value, param.entryUnit || 's')
                    .subtract(days, 'day')
                    .subtract(hours, 'hour')
                    .minutes()
                );
            }
        }

        // if minutes or report and there is no hours
        if ((param.minutes || (param.report && hours < 1)) && minutes !== 0) {
            let minuteString: string;
            if (minutes < 10) {
                minuteString = '0' + minutes;
            } else {
                minuteString = String(minutes);
            }

            formattedDuration.push(minuteString, this.minUnit);
        }

        // if seconds or report and there is no hours and no minutes
        if (param.seconds || (param.report && hours < 1 && minutes < 1)) {
            let secondString: string;
            if (duration.seconds() < 10) {
                secondString = '0' + duration.seconds();
            } else {
                secondString = String(duration.seconds());
            }
            formattedDuration.push(secondString, this.secondsUnit);
        }

        if (!formattedDuration.length) {
            const shortestUnit: string = param.seconds ? this.secondsUnit : param.minutes ? ' min' : this.hourUnit;

            if (shortestUnit === this.hourUnit) {
                formattedDuration.push('0' + shortestUnit);
            } else {
                formattedDuration.push('0', shortestUnit);
            }
        }

        return formattedDuration.join(' ');
    }
}

export class DurationPipeParam {
    days?: boolean = false;
    // Full decomposition of duration
    // Example 7000 min give 4j 20h 40min
    fullDecomposition?: boolean = false;
    // display minutes
    minutes: boolean = true;

    // display seconds
    seconds: boolean = true;

    // override parameter minutes or seconds to true when all superior and displayable unit are 0
    //
    // example with param {report : true, minutes : true, seconds : false} :
    // `10` will be translate `10 s` => second is disable but we does not want display 0 minute
    // `60` will be translate `01 min` => there is a minute, so we use the `seconds` parameter
    // `4600` will be translate `1h 16 min`
    //
    //
    // example with param {report : false, minutes : true, seconds : false} :
    // `10` will be translate `0 min` => second is disable but we cannot display 0 minute
    // `60` will be translate `01 min` => there is a minute, so we do not display second
    // `4600` will be translate `1h 16 min`
    report: boolean = false;

    // unit of the receive value, default is in seconds
    entryUnit: DurationConstructor = 's';
}
